import { z } from 'zod/mini';
import type { LatLng } from './geo';
import { DEFAULT_REGION_ID, REGION_IDS, getRegion } from './regions';

export const MOVEMENT_MODES = ['move', 'pan', 'frozen'] as const;
export type MovementMode = (typeof MOVEMENT_MODES)[number];

export const MOVEMENT_MODE_INFO: Record<MovementMode, { label: string; description: string }> = {
  move: { label: 'Roam', description: 'Walk down the road, look around, zoom in.' },
  pan: { label: 'Look around', description: 'Turn and zoom, but stay where you landed.' },
  frozen: { label: 'Snapshot', description: 'One fixed view. No moving, no turning.' },
};

export const MIN_ROUNDS = 1;
export const MAX_ROUNDS = 20;
/** Seconds per round; 0 means no limit. */
export const TIME_LIMIT_OPTIONS = [0, 30, 60, 90, 120, 180, 300] as const;

/**
 * 'pin': the closer the pin, the more points. 'country': only the country counts. 'combo':
 * points for the right continent and country, plus closeness.
 */
export const GAME_MODES = ['pin', 'country', 'combo'] as const;
export type GameMode = (typeof GAME_MODES)[number];

export const GAME_MODE_INFO: Record<GameMode, { label: string; description: string }> = {
  pin: { label: '📍 Pin', description: 'Drop a pin: the closer, the more points.' },
  country: { label: '🏳️ Country', description: 'Just find the country: full points if right, none if not.' },
  combo: {
    label: '🧩 Combo',
    description: '1,000 points for the right continent, 1,000 more for the right country, up to 3,000 for closeness.',
  },
};

/** Country and combo modes need a map with several countries to choose from. */
export function allowsCountryMode(regionId: string): boolean {
  return (getRegion(regionId)?.countries.length ?? 0) > 1;
}

/** The settings as played: country and combo modes fall back to pins on a single-country map. */
export function playableSettings<T extends { regionId: string; mode: GameMode }>(settings: T): T {
  return settings.mode !== 'pin' && !allowsCountryMode(settings.regionId) ? { ...settings, mode: 'pin' } : settings;
}

export const gameSettingsSchema = z.object({
  regionId: z.enum(REGION_IDS),
  rounds: z.int().check(z.minimum(MIN_ROUNDS), z.maximum(MAX_ROUNDS)),
  timeLimitSec: z.int().check(z.minimum(0), z.maximum(600)),
  movement: z.enum(MOVEMENT_MODES),
  // Optional when parsing, so settings saved before modes existed still load.
  mode: z._default(z.enum(GAME_MODES), 'pin'),
});

export type GameSettings = z.infer<typeof gameSettingsSchema>;

export const DEFAULT_SETTINGS: GameSettings = {
  regionId: DEFAULT_REGION_ID,
  rounds: 5,
  timeLimitSec: 0,
  movement: 'move',
  mode: 'pin',
};

/** How long the "Round N" intro plays before the clock starts. */
export const ROUND_INTRO_MS = 3200;
/** Extra time granted to late guesses to absorb network latency. */
export const GUESS_GRACE_MS = 1500;

/** Everything a client needs to display a panorama, without revealing coordinates. */
export interface PanoRef {
  panoId: string;
  heading: number;
}

export interface RevealedLocation extends LatLng {
  panoId: string;
  countryCode: string;
}

export const latLngSchema = z.object({
  lat: z.number().check(z.minimum(-90), z.maximum(90)),
  lng: z.number().check(z.minimum(-1080), z.maximum(1080)),
});
